import { supabase } from '../lib/supabase';
import CryptoJS from 'crypto-js';

/**
 * Signature Service
 * Handles contract signature operations with verification and audit trails
 */

export const signatureService = {
  /**
   * Create a new signature for a contract
   */
  async createSignature(contractId, signatureData, signerInfo) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('غير مصرح');

      // Generate signature hash for verification
      const signatureHash = CryptoJS?.SHA256(signatureData)?.toString();

      // Get client IP and user agent
      const ipAddress = await this.getClientIP();
      const userAgent = navigator?.userAgent || 'Unknown';

      const { data, error } = await supabase?.from('contract_signatures')?.insert({
          contract_id: contractId,
          signer_id: user?.id,
          signer_name: signerInfo?.name,
          signer_email: signerInfo?.email,
          signer_type: signerInfo?.type,
          signature_data: signatureData,
          signature_hash: signatureHash,
          ip_address: ipAddress,
          user_agent: userAgent,
          status: 'signed'
        })?.select()?.single();

      if (error) throw error;

      // Upload signature image to storage
      await this.uploadSignatureImage(user?.id, data?.id, signatureData);

      return {
        success: true,
        signature: {
          id: data?.id,
          contractId: data?.contract_id,
          signerId: data?.signer_id,
          signerName: data?.signer_name,
          signerEmail: data?.signer_email,
          signerType: data?.signer_type,
          signatureHash: data?.signature_hash,
          signedAt: data?.signed_at,
          status: data?.status
        }
      };
    } catch (error) {
      console.error('Error creating signature:', error);
      throw new Error(error?.message || 'فشل في إنشاء التوقيع');
    }
  },

  /**
   * Get all signatures for a contract
   */
  async getContractSignatures(contractId) {
    try {
      const { data, error } = await supabase?.from('contract_signatures')?.select('*')?.eq('contract_id', contractId)?.order('signed_at', { ascending: false });

      if (error) throw error;

      return data?.map(sig => ({
        id: sig?.id,
        contractId: sig?.contract_id,
        signerId: sig?.signer_id,
        signerName: sig?.signer_name,
        signerEmail: sig?.signer_email,
        signerType: sig?.signer_type,
        signatureHash: sig?.signature_hash,
        ipAddress: sig?.ip_address,
        signedAt: sig?.signed_at,
        status: sig?.status
      })) || [];
    } catch (error) {
      console.error('Error fetching signatures:', error);
      throw new Error(error?.message || 'فشل في جلب التوقيعات');
    }
  },

  /**
   * Verify signature integrity
   */
  async verifySignature(signatureId, signatureData) {
    try {
      const { data, error } = await supabase?.from('contract_signatures')?.select('signature_hash')?.eq('id', signatureId)?.single();

      if (error) throw error;

      const currentHash = CryptoJS?.SHA256(signatureData)?.toString();
      const isValid = currentHash === data?.signature_hash;

      // Log verification attempt
      await this.createAuditEntry(signatureId, 'signature_verified', {
        verified: isValid,
        timestamp: new Date()?.toISOString()
      });

      return {
        success: true,
        verified: isValid,
        message: isValid ? 'التوقيع صحيح' : 'التوقيع غير صحيح'
      };
    } catch (error) {
      console.error('Error verifying signature:', error);
      throw new Error(error?.message || 'فشل في التحقق من التوقيع');
    }
  },

  /**
   * Get signature audit trail
   */
  async getSignatureAuditTrail(signatureId) {
    try {
      const { data, error } = await supabase?.from('signature_audit_trail')?.select('*')?.eq('signature_id', signatureId)?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(entry => ({
        id: entry?.id,
        signatureId: entry?.signature_id,
        action: entry?.action,
        performedBy: entry?.performed_by,
        ipAddress: entry?.ip_address,
        metadata: entry?.metadata,
        createdAt: entry?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching audit trail:', error);
      throw new Error(error?.message || 'فشل في جلب سجل التدقيق');
    }
  },

  /**
   * Create audit trail entry
   */
  async createAuditEntry(signatureId, action, metadata = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const ipAddress = await this.getClientIP();

      const { error } = await supabase?.from('signature_audit_trail')?.insert({
          signature_id: signatureId,
          action,
          performed_by: user?.id,
          ip_address: ipAddress,
          metadata
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error creating audit entry:', error);
    }
  },

  /**
   * Upload signature image to storage
   */
  async uploadSignatureImage(userId, signatureId, signatureDataUrl) {
    try {
      // Convert data URL to blob
      const response = await fetch(signatureDataUrl);
      const blob = await response?.blob();

      const filePath = `${userId}/${signatureId}.png`;

      const { error } = await supabase?.storage?.from('contract-signatures')?.upload(filePath, blob, {
          contentType: 'image/png',
          upsert: true
        });

      if (error) throw error;

      return { success: true, filePath };
    } catch (error) {
      console.error('Error uploading signature image:', error);
      throw new Error('فشل في رفع صورة التوقيع');
    }
  },

  /**
   * Get signature image URL
   */
  async getSignatureImageUrl(userId, signatureId) {
    try {
      const filePath = `${userId}/${signatureId}.png`;

      const { data, error } = await supabase?.storage?.from('contract-signatures')?.createSignedUrl(filePath, 3600); // 1 hour expiry

      if (error) throw error;

      return data?.signedUrl;
    } catch (error) {
      console.error('Error getting signature URL:', error);
      return null;
    }
  },

  /**
   * Get client IP address
   */
  async getClientIP() {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response?.json();
      return data?.ip || 'Unknown';
    } catch (error) {
      return 'Unknown';
    }
  },

  /**
   * Check if contract has all required signatures
   */
  async checkContractSignatureStatus(contractId) {
    try {
      const signatures = await this.getContractSignatures(contractId);

      const hasClientSignature = signatures?.some(sig => sig?.signerType === 'client' && sig?.status === 'signed');
      const hasStaffSignature = signatures?.some(sig => sig?.signerType === 'staff' && sig?.status === 'signed');

      return {
        success: true,
        hasClientSignature,
        hasStaffSignature,
        isFullySigned: hasClientSignature && hasStaffSignature,
        totalSignatures: signatures?.length || 0,
        signatures
      };
    } catch (error) {
      console.error('Error checking signature status:', error);
      throw new Error(error?.message || 'فشل في التحقق من حالة التوقيعات');
    }
  }
};

export default signatureService;